<header
  class="nav flex items-center justify-center box-border h-nav md:h-nav-desktop px-1 [@media(min-width:360px)]:px-4 bg-surface-header text-text-on-header dark:border-b dark:border-border-on-header"
  [class.!bg-status-warn]="impersonating$ | async"
  [class.text-text-primary]="(sessionService.session$ | async) === null"
>
  <div class="flex flex-row items-center gap-1 h-full max-w-content w-full">
    @if ((sessionService.session$ | async) && !nav.currentRouteSignal().startsWith('/getting-started')) {
      @if (!monthSelectorTakeoverOpen()) {
        @let activeNavItem = activeNavTab();
        <nav appUiPrimaryNavigation aria-label="Primary navigation">
          <a
            appUiPrimaryNavigationItem
            variant="brand"
            [active]="activeNavItem === 'budgets'"
            aria-label="Budgets"
            routerLink="/budgets"
          >
            <app-ui-icon class="h-9 w-9 shrink-0" name="logo" />
            <span class="nav-brand-label max-md:hidden text-lg font-semibold leading-none tracking-[-0.01em]"
              >Budgets</span
            >
          </a>
          <button
            appUiPrimaryNavigationItem
            class="nav-sort"
            [active]="activeNavItem === 'transactions'"
            type="button"
            [attr.aria-label]="transactionsAriaLabel()"
            (click)="navigateToTransactions(unsortedTransactionsCount())"
          >
            <app-ui-icon
              class="nav-tab-icon size-nav-transaction-icon shrink-0 md:hidden"
              name="transaction-ledger"
            />
            <span class="nav-tab-label max-md:hidden text-sm font-semibold leading-none">Transactions</span>
            @if (unsortedTransactionsCount(); as unsortedTransactionsCount) {
              <app-header-count-badge
                class="nav-sort-count absolute top-nav-badge-offset right-nav-badge-offset"
              />
            }
          </button>
          @if (reportsEnabled()) {
            <a
              appUiPrimaryNavigationItem
              class="nav-reports"
              [active]="activeNavItem === 'reports'"
              aria-label="Reports"
              routerLink="/reports"
            >
              <app-ui-icon class="nav-tab-icon size-nav-accounts-icon shrink-0 md:hidden" name="chart-column" />
              <span class="nav-tab-label max-md:hidden text-sm font-semibold leading-none">Reports</span>
            </a>
          }
          <a
            appUiPrimaryNavigationItem
            class="nav-accounts"
            [active]="activeNavItem === 'accounts'"
            [attr.aria-label]="accountsAriaLabel()"
            routerLink="/accounts"
          >
            <app-ui-icon
              class="nav-tab-icon size-nav-accounts-icon shrink-0 md:hidden"
              name="wallet-cards"
            />
            <span class="nav-tab-label max-md:hidden text-sm font-semibold leading-none">Accounts</span>
            @if (deauthenticatedCredentialsCount(); as deauthenticatedCredentialsCount) {
              <app-header-count-badge
                class="nav-accounts-count absolute top-nav-badge-offset right-nav-badge-offset"
                tone="danger"
              />
            }
          </a>
        </nav>

        <div class="spacer"></div>

        @if (currentMonthYear$ | async; as currentMonthYear) {
          @if (month.showSelectorSignal()) {
            <!-- The full selector needs more room once a fourth tab (Reports) is in the header. -->
            <div class="nav-month-pill-wrapper" [class]="reportsEnabled() ? 'nav-four-full:hidden' : 'nav-full:hidden'">
              <button
                appUiButton
                appearance="outline"
                size="header"
                surface="header"
                shape="pill"
                class="nav-month-pill leading-none"
                (click)="monthSelectorTakeoverOpen.set(true)"
              >
                {{ month.formatMonthYear(currentMonthYear) }}
              </button>
            </div>
            <div [class]="reportsEnabled() ? 'nav-four-compact:hidden' : 'nav-compact:hidden'">
              <app-month-selector [currentMonthYear]="currentMonthYear" />
            </div>
          }
        }

        <button
          class="nav-profile inline-flex h-nav-home w-nav-home items-center justify-center p-0 border-0 bg-transparent rounded-full relative focus-visible:shadow-focus focus-visible:outline-none"
          type="button"
          aria-label="Open profile menu"
          [appUiDropdownMenuTrigger]="profileMenu"
        >
          <div
            class="flex h-avatar w-avatar items-center justify-center rounded-full text-center text-xs font-extrabold tracking-[0.04em] border-2 border-white/35 text-text-on-header"
          >
            {{ initials$ | async }}
          </div>
        </button>
        <ng-template #profileMenu>
          <div
            appUiDropdownMenu
            class="nav-menu min-w-[224px] p-0 rounded-lg overflow-hidden border-border-default shadow-overlay"
            aria-label="Profile menu"
          >
            @if (userService.user$ | async; as user) {
              <div class="px-6 py-4 border-b border-border-muted">
                <div class="text-text-title text-body-sm font-semibold leading-tight">
                  {{ user.firstName }} {{ user.lastName }}
                </div>
                <div class="text-text-muted text-body-xs leading-[1.4] mt-0.5">{{ user.email }}</div>
              </div>
            }
            <a
              appUiDropdownMenuItem
              class="nav-settings min-h-11 py-2.5 px-6 gap-2 text-text-title text-body-sm"
              routerLink="/account/settings"
            >
              <app-ui-icon class="text-text-title" name="settings" />
              <span>Settings</span>
            </a>
            <app-support #supportMenu>
              <button
                appUiDropdownMenuItem
                class="nav-get-help min-h-11 py-2.5 px-6 gap-2 text-text-title text-body-sm"
                (click)="supportMenu.openSupportDialog()"
              >
                <app-ui-icon class="text-text-title" name="envelope" />
                <span>Get help</span>
              </button>
            </app-support>
            <button
              appUiDropdownMenuItem
              class="min-h-11 py-2.5 px-6 gap-2 text-text-title text-body-sm"
              (click)="logout()"
            >
              <app-ui-icon class="text-text-title" name="transfer-out" />
              <span>Sign out</span>
            </button>
            @if ((userService.user$ | async)?.superAdmin) {
              <button
                appUiDropdownMenuItem
                class="min-h-11 py-2.5 px-6 gap-2 text-text-title text-body-sm"
                (click)="navigateTo(['/admin'])"
              >
                <app-ui-icon class="text-text-title" name="cogs" />
                <span>Admin</span>
              </button>
            }
            @if (impersonating$ | async) {
              <button
                type="button"
                appUiDropdownMenuItem
                class="min-h-11 py-2.5 px-6 gap-2 text-text-title text-body-sm"
                (click)="userService.endImpersonation()"
              >
                <app-ui-icon class="text-text-title" name="transfer-out" />
                <span>End impersonation</span>
              </button>
            }
          </div>
        </ng-template>
      } @else {
        <a
          class="inline-flex items-center justify-center w-10 h-10 p-0 border-0 bg-transparent rounded-full relative focus-visible:shadow-focus focus-visible:outline-none"
          aria-label="Budgets home"
          routerLink="/budgets"
        >
          <app-ui-icon class="h-8 w-8" name="logo" />
        </a>
        <div class="spacer"></div>
        <app-month-selector [currentMonthYear]="(currentMonthYear$ | async) ?? ''" />
        <button
          class="inline-flex items-center justify-center w-10 h-10 p-0 border-0 bg-transparent rounded-full relative cursor-pointer focus-visible:shadow-focus focus-visible:outline-none"
          type="button"
          aria-label="Close month selector"
          (click)="monthSelectorTakeoverOpen.set(false)"
        >
          <app-ui-icon class="h-9 w-9" name="close" />
        </button>
      }
    } @else {
      <div class="inline-flex h-10 w-10 items-center justify-center" aria-hidden="true">
        <app-ui-icon class="h-8 w-8" name="logo" />
      </div>

      <h1 class="relative top-0.5 text-base [@media(min-width:340px)]:text-[22px] font-extrabold text-text-on-header">
        {{ nav.titleSignal() }}
      </h1>
    }
  </div>
</header>
